/**
 * @module UserApiService
 * @remarks RTK Query service for user and customer data management
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
  Profile,
  UserDataResponse,
  RawUserDataResponse,
  CustomerDataResponse,
  FilialeDataResponse,
  RequestPasswordResetRequest,
  RequestPasswordResetResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  UpdateLoadFeUmsaetzeRequest,
} from '@models/user.model'
import { Customer, Kundenkarte } from '@models/customer.model'
import { FilialeData } from '@models/filialien.model'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@store/store'
import { log } from '@logger'

/**
 * Enhanced fetchBaseQuery with API tracking
 */
const trackedBaseQuery = fetchBaseQuery({
  baseUrl: '/api/',
  prepareHeaders: (headers) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      headers.set('Authorization', token)
    }
    return headers
  },
})

/**
 * Wrapper for fetchBaseQuery that adds API call tracking
 */
const baseQueryWithTracking: typeof trackedBaseQuery = async (
  args,
  api,
  extraOptions
) => {
  const method = typeof args === 'string' ? 'GET' : args.method || 'GET'
  const url = typeof args === 'string' ? args : args.url

  // Start tracking the API call
  const trackingId = log.apiStart(
    method,
    `/api/${url}`,
    typeof args === 'object' ? args.body : undefined,
    { component: 'userApi' }
  )

  try {
    const result = await trackedBaseQuery(args, api, extraOptions)

    if (result.error) {
      // Mark as failed
      log.apiFail(trackingId, result.error)
    } else {
      // Mark as completed
      log.apiComplete(trackingId, result.data)
    }

    return result
  } catch (error) {
    // Mark as failed
    log.apiFail(trackingId, error)
    throw error
  }
}

/**
 * RTK Query API service for user and customer data operations
 */
export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: baseQueryWithTracking,
  tagTypes: ['User', 'CustomerProfile', 'FilialeProfile'],
  endpoints: (builder) => ({
    /**
     * Endpoint for retrieving user data
     */
    getUserData: builder.query<UserDataResponse, string>({
      query: (userId) => ({
        url: 'data/users',
        method: 'POST',
        body: {
          where: { id: userId },
          fields: {
            '0': 'kunden',
            '1': 'filialen',
          },
        },
      }),
      providesTags: ['User'],
      transformResponse: (response: unknown): UserDataResponse => {
        if (
          !Array.isArray(response) ||
          response.length === 0 ||
          !response[0].users
        ) {
          throw new Error('No user data found')
        }

        const userData = response[0].users

        // Check if userData.kunden is null
        // If yes, return a special response (kunden === null is our special case)
        if (userData.kunden === null) {
          return {
            id: '', // Empty ID since no customer exists
            lastLogin: new Date().toISOString(),
            filialen: userData.filialen, // Can be null
            // Return original data so MissingCustomerModal can detect it
            rawUsers: userData,
          }
        }

        // Normal case when customer data is available
        return {
          id: userData.kunden ? userData.kunden.id : '',
          lastLogin: new Date().toISOString(),
          filialen: userData.filialen,
          rawUsers: userData, // Keep original data
        }
      },
    }),

    /**
     * Endpoint for retrieving customer data
     */
    getCustomerData: builder.query<CustomerDataResponse, string>({
      query: (customerId) => {
        // Ensure customerId is a string and handle edge cases
        const customerIdStr = customerId ? String(customerId) : '';
        const numericId = customerIdStr.replace(/[^0-9]/g, '');
        
        return {
          url: 'data/kunden',
          method: 'POST',
          body: {
            where: { id: Number(numericId) || 0 },
            fields: {
            '0': 'anrede',
            '1': 'vorname',
            '2': 'nachname',
            '3': 'e_mail',
            '4': 'telefon',
            '5': 'strasse_und_hausnummer',
            '6': 'adresszusatz',
            '7': 'plz',
            '8': 'ort',
            '9': 'land',
            '10': 'kundenkommunikation',
            '11': 'dokumenten_zuordnung',
            '12': 'name',
            '13': 'aktiv_inaktiv',
            '14': 'werbung_import',
            '15': 'geburtsdatum',
            '16': 'erstellt_von_import',
            '17': 'erstelldatum',
            '18': 'bildpfad',
            '19': 'updatedatum',
            '20': 'beschwerde_loeschen',
            '21': 'activ',
            '22': 'bemerkung',
            '23': 'anmeldedatum_import',
            '24': 'bonusjahranfang',
            '25': 'bonusjahrende',
            '26': 'bonusjahrnr',
            '27': 'anrede_import',
            '28': 'bildname',
            '29': 'kundennummer_alt',
            '30': 'aktivitaeten',
            '31': 'einwilligungen',
            '32': 'kundenkarten',
            '33': 'kundenumsaetze',
            '34': 'kundennummer',
            '35': 'lieblings_filiale',
            '36': 'alert_birthday',
            '37': 'trigger_birthday_check',
            '38': 'migrierter_datensatz',
            '39': 'lieblingsmarken',
            '40': 'lieblingsfiliale',
            '41': 'auswahl',
            '42': 'formel_4',
            '43': 'fullname',
            '44': 'kundenbetreuung',
            '45': 'mailadresse_ungueltig_alt',
            '46': 'selektionen',
            '47': 'ihre_daten_im_Überblick',
            '48': 'historie',
            '49': 'matches',
            '50': 'matches_array_nr',
            '51': 'duplicatedelete',
            '52': 'duplicat_gefunden',
            '53': 'duplicate_warning',
            '54': 'users',
            '55': 'zuweisung_kontakt',
            '56': 'kunde_kundin',
            '57': 'email_eingang',
            '58': 'email_eingang2',
            '59': 'new_email',
            '60': 'kundenbetreuung2',
            '61': 'kundenbetreuung3',
            '62': 'geburtsmonat',
            '63': 'teilnahmebedingungen',
            '64': 'datenschutz',
            '65': 'marketing',
            '66': 'archiv_kundenumsaetze',
            '67': 'helper_anrede',
            '68': 'newsletter',
            '69': 'geaendert_von',
            '70': 'kundendaten_geaendert',
            '71': 'helper_tillhub_export',
            '72': 'mail_erneut',
            '73': 'einwilligung_newsletter',
            '74': 'einwilligung_marketing',
            '75': 'einwilligung_teilnahme',
            '76': 'einwilligung_datenschutz',
            '77': 'widerruf_newsletter',
            '78': 'widerruf_teilnahme',
            '79': 'widerruf_marketing',
            '80': 'widerruf_datenschutz',
            '81': 'helper_konzept',
            '82': 'addresscheck',
            '83': 'in_selektion_enthalten_danach_loeschen',
            '84': 'post_nicht_zustellbar',
            '85': 'umsatz_last24m',
            '86': 'umsatz_last12m',
            '87': 'helper_less2years_registered',
            '88': 'formel',
            '89': 'vk_bought_product',
            '90': 'eingeloester_voucher',
            '91': 'formel_3',
            '92': 'formel_5',
            '93': 'bonus_2013',
            '94': 'bonus_2014',
            '95': 'bonus_2015',
            '96': 'bonus_2016',
            '97': 'bonus_2017',
            '98': 'bonus_2018',
            '99': 'bonus_2019',
            '100': 'bonus_2020',
            '101': 'bonus_2021',
            '102': 'bonus_2022',
            '103': 'bonus_2023',
            '104': 'bonus_2024',
            '105': 'bonus_2025',
            '106': 'partof_bonus_selection',
            '107': 'bonus_selection',
            '108': 'menu_umsaetze',
            '109': 'bonusjahre',
            '110': 'helper_erneuter_bonus_versand',
            '111': 'anpinnen',
            '112': 'alle_artikel_ids_in_dashboard_rahmen',
            '113': 'alle_ids_in_den_umsaetzen',
            '114':
              'artikel_ids_ausserhalb_der_nicht_gekauften_dashboard_produkte',
            '115': 'kundenselektionen',
            '116': 'hinweis_bonusjahr',
            '117': 'kundenpromotions',
            '118': 'all_card_numbers',
            '119': 'doublette_loesen',
            '120': 'geschlecht',
            '121': 'zu_brevo_migriert',
            '122': 'changes_for_brevo',
            '123': 'e_mail_duplikat',
            '124': 'kundenkarte_duplikat',
            '125': 'revenue_count',
            '126': 'comment_tillhub',
            '127': 'customer_notes_tillhub',
            '128': 'tillhub_customer_id',
            '129': 'umbuchen_auf_folgende_kundennummer',
            '130': 'gba_lert_this_year',
            '131': 'nachbuchungen',
            '132': 'trigger_create_card',
            '133': 'brand_create_card',
            '134': 'fe_umsaetze',
            '135': 'load_fe_umsaetze',
            '136': 'marke',
            kundenkarten: ['punktestand', 'kartennummer', 'status'],
            kundenpromotions: [
              'id',
              'promotion_id', 
              'kunde',
              'redeemed',
              'kundenbetreuung',
              'ja_nein',
              'text',
              'valid',
              'active',
              'required_points',
              'activated',
              'status',
              'list_points',
              'latest_validation'
            ],
            },
          },
        };
      },
      providesTags: ['CustomerProfile'],
      transformResponse: (response: Customer): CustomerDataResponse => {
        if (
          !Array.isArray(response) ||
          response.length === 0 ||
          !response[0].kunden
        ) {
          throw new Error('Keine Kundendaten gefunden')
        }

        const customerData = response[0].kunden
        const profile: Profile = {
          title: customerData.titel || '',
          customer_id: response[0]?.id,
          customer_number: customerData.kundennummer || '',
          points:
            Number(
              customerData.kundenkarten?.filter(
                (karte: Kundenkarte) => 'punktestand' in karte
              )?.[0]?.punktestand
            ) || 0,
          customer_card_number:
            Number(
              customerData.kundenkarten?.filter(
                (karte: Kundenkarte) => 'kartennummer' in karte
              )?.[0]?.kartennummer
            ) || 0,
          firstname: customerData.vorname || '',
          lastname: customerData.nachname || '',
          salutation: customerData.anrede === 1 ? 'Herr' : 'Frau',
          phone: customerData.telefon || '',
          street: customerData.strasse_und_hausnummer || '',
          zip: customerData.plz || '',
          city: customerData.ort || '',
          country: customerData.land || '',
          email: customerData.e_mail || '',
          birthday: customerData.geburtsdatum || '',
          gender: customerData.geschlecht === 1 ? 'Weiblich' : 'Männlich',
          created_at: customerData.erstelldatum || '',
          revenue_count: customerData.revenue_count
            ? Number(customerData.revenue_count)
            : 0,
          promotions: customerData.kundenpromotions,
          customer_card_id: customerData.kundenkarten?.[0]?.id || '',
          einwilligungen: customerData.einwilligungen || null,
          fe_umsaetze: customerData.fe_umsaetze || null,
        }

        return { profile }
      },
      keepUnusedDataFor: 3600,
    }),

    /**
     * Endpoint for retrieving filiale data
     */
    getFilialeData: builder.query<FilialeDataResponse, string>({
      query: (filialeId) => {
        // Extrahiere die numerische ID (entferne Z-Präfix falls vorhanden)
        const numericId = filialeId.replace(/^Z/, '')
        const parsedId = parseInt(numericId, 10)
        return {
          url: 'data/filialen',
          method: 'POST',
          body: {
            where: { id: parsedId }, // Sende als Zahl, nicht als String
            fields: [],
          },
        }
      },
      providesTags: ['FilialeProfile'],
      transformResponse: (response: FilialeData[]): FilialeDataResponse => {
        if (
          !Array.isArray(response) ||
          response.length === 0 ||
          !response[0].filialen
        ) {
          console.error('[getFilialeData] No filiale data found in response')
          throw new Error('Keine Filialedaten gefunden')
        }

        const filialeData = response[0].filialen

        const profile = {
          filiale_id: response[0].id,
          filialnummer: filialeData.filialnummer,
          title_filiale: filialeData.title_filiale,
          marke: filialeData.marke,
          strasse_hausnummer: filialeData.strasse_hausnummer,
          plz: filialeData.plz,
          ort: filialeData.ort,
          bundesland: filialeData.bundesland,
          c_o: filialeData.c_o,
          telefon: filialeData.telefon,
          vertriebsleitung: filialeData.vertriebsleitung,
          bereichsmanagement: filialeData.bereichsmanagement,
          ansprechpartner: filialeData.ansprechpartner,
          marke_konzept_alt: filialeData.marke_konzept_alt,
          betriebsnummer: filialeData.betriebsnummer,
          status_import: filialeData.status_import,
          status: filialeData.status,
          marke_import: filialeData.marke_import,
          e_mail: filialeData.e_mail,
          abfragen_dashboard: filialeData.abfragen_dashboard,
          created_at: new Date().toISOString(),
          users: filialeData.users,
        }
        return { profile }
      },
      keepUnusedDataFor: 3600,
    }),

    /**
     * Endpoint for updating the loadFeUmsaetze setting
     */
    updateLoadFeUmsaetze: builder.mutation<
      UpdateLoadFeUmsaetzeRequest,
      UpdateLoadFeUmsaetzeRequest
    >({
      query: (credentials) => ({
        url: `data/kunden/${credentials.customerId}`,
        method: 'PATCH',
        body: {
          fields: {
            loadFeUmsätze: credentials.loadFeUmsaetze,
          },
        },
      }),
    }),

    /**
     * Request password reset (Step 1)
     */
    requestPasswordReset: builder.mutation<
      RequestPasswordResetResponse,
      RequestPasswordResetRequest
    >({
      query: (credentials) => ({
        url: 'auth?path=auth/resetPassword',
        method: 'POST',
        body: credentials,
      }),
    }),

    /**
     * Reset password with validation code (Step 2)
     */
    resetPassword: builder.mutation<
      ResetPasswordResponse,
      ResetPasswordRequest
    >({
      query: (credentials) => ({
        url: 'auth?path=auth/resetPassword2',
        method: 'POST',
        body: credentials,
      }),
    }),

    /**
     * Endpoint for retrieving detailed promotion data based on customer promotions
     */
    getPromotionDetails: builder.query<any[], string>({
      async queryFn(customerId, { getState }, extraOptions, baseQuery) {
        try {
          const state = getState() as RootState;
          
          // First get promotion IDs from customer data
          const customerPromotions = selectCustomerPromotionsFromUserApi(state);
          
          if (!customerPromotions || customerPromotions.length === 0) {
            return { data: [] };
          }
          
          // Extract unique promotion IDs
          const promotionIds = customerPromotions
            .filter((promo: any) => promo.promotion_id !== undefined)
            .map((promo: any) => promo.promotion_id?.toString())
            .filter(Boolean) as string[];
          
          if (!promotionIds.length) {
            return { data: [] };
          }
          
          // Fetch details for each promotion
          const promotionsData = await Promise.all(
            promotionIds.map(async (promotionId) => {
              const result = await baseQuery({
                url: "data/promotions",
                method: "POST",
                body: {
                  where: {
                    id: promotionId
                  },
                  fields: []
                },
              });
              
              if (result.error) {
                return null;
              }
              
              return result.data;
            })
          );
          
          // Filter out failed requests and flatten
          const validPromotions = promotionsData
            .filter(Boolean)
            .flat();
            
          return { data: validPromotions };
        } catch (error) {
          return { 
            error: { 
              status: 'CUSTOM_ERROR', 
              data: String(error),
              error: String(error)
            } as any 
          };
        }
      },
      providesTags: ['CustomerProfile'],
      keepUnusedDataFor: 3600,
    }),

    /**
     * Mutation for manually invalidating the cache
     */
    invalidateUserData: builder.mutation<void, void>({
      queryFn: () => ({ data: undefined }),
      invalidatesTags: ['User', 'CustomerProfile', 'FilialeProfile'],
    }),
  }),
})

const selectUserDataResult = userApi.endpoints.getUserData.select

/**
 * Selector for retrieving branch office data from the state
 */
export const selectFilialen = (state: RootState) => {
  // Find all getUserData queries in the cache
  const queries = state.userApi.queries

  // Search for the first successful query
  for (const queryKey in queries) {
    if (queryKey.startsWith('getUserData') && queries[queryKey]?.data) {
      const data = queries[queryKey].data as unknown

      // Check if data matches the transformed UserDataResponse structure
      if (data && typeof data === 'object' && 'filialen' in data) {
        return (data as UserDataResponse).filialen || null
      }

      // Check if data has the raw response structure with users property
      if (data && typeof data === 'object' && 'users' in data) {
        const rawData = data as RawUserDataResponse
        if (rawData.users && 'filialen' in rawData.users) {
          return rawData.users.filialen || null
        }
      }
    }
  }

  return null // Fallback: null
}

/**
 * Selector for extracting basic user details
 */
export const selectUserDetails = createSelector(
  [(state, userId) => selectUserDataResult(userId)(state)?.data],
  (userData) => ({
    id: userData?.id || '',
    lastLogin: userData?.lastLogin || '',
  })
)

/**
 * Selector for extracting customer promotions from userApi cache
 */
export const selectCustomerPromotionsFromUserApi = (state: RootState) => {
  const queries = state.userApi.queries

  // Look for successful getCustomerData queries
  for (const queryKey in queries) {
    if (queryKey.startsWith('getCustomerData') && queries[queryKey]?.data) {
      const data = queries[queryKey].data as any
      if (data?.profile?.promotions) {
        return data.profile.promotions
      }
    }
  }
  
  return null
}

/**
 * Selector for extracting promotion activation data from userApi cache
 */
export const selectPromotionActivationFromUserApi = (state: RootState, customerId: string) => {
  const promotions = selectCustomerPromotionsFromUserApi(state)
  
  if (!promotions) return []
  
  // Transform promotions to match PromotionActivation format
  return promotions.map((promo: any) => ({
    id: promo.id,
    md: 0,
    kundenpromotions: {
      promotion_id: promo.promotion_id,
      kunde: { id: customerId },
      redeemed: promo.redeemed,
      kundenbetreuung: promo.kundenbetreuung,
      ja_nein: promo.ja_nein,
      text: promo.text,
      valid: promo.valid,
      active: promo.active,
      required_points: promo.required_points,
      list_points: promo.list_points,
      latest_validation: promo.latest_validation,
      activated: promo.activated,
      status: promo.status,
    }
  }))
}

export const {
  useGetUserDataQuery,
  useGetCustomerDataQuery,
  useGetFilialeDataQuery,
  useGetPromotionDetailsQuery,
  useRequestPasswordResetMutation,
  useResetPasswordMutation,
  useInvalidateUserDataMutation,
  useUpdateLoadFeUmsaetzeMutation,
} = userApi

// Selectors are already exported above
